<template>
  <b-form id="product-form" @submit.stop.prevent>
    <b-form-group
      label-cols-sm="3"
      content-cols-md="9"
      label-align-sm="right"
      label-class="gk-label-margin"
      :label="$gettext('Description*')"
    >
      <b-form-input v-model="item.description" required />
    </b-form-group>

    <b-form-group
      label-cols-sm="3"
      content-cols-md="6"
      label-align-sm="right"
      label-class="gk-label-margin"
      :label="$gettext('Amount*')"
    >
      <b-input-group>
        <b-form-input v-model="item.amount" type="number" required />
        <b-form-select v-model="item.amount_is_gross" :options="priceOptions" required />
      </b-input-group>
    </b-form-group>

    <b-form-group
      label-cols-sm="3"
      content-cols-md="6"
      label-align-sm="right"
      label-class="gk-label-margin"
      :label="$gettext('Tax rate*')"
    >
      <VatInput v-model="item" countryCode="currentCompany.country" />
    </b-form-group>

    <b-form-group
      label-cols-sm="3"
      content-cols-md="6"
      label-align-sm="right"
      label-class="gk-label-margin"
      :label="$gettext('Group')"
    >
      <b-form-input v-if="showCreateGroupInput" v-model="newGroup.name" required />
      <b-form-select
        v-else
        v-model="item.group"
        :options="groupOptions"
        value-field="id"
        text-field="name"
      />
      <div v-if="allowGroupCreation" class="d-flex justify-content-end">
        <GekkoButton
          v-if="!showCreateGroupInput"
          :icon="'fa-solid fa-plus'"
          :text="$gettext('Create new')"
          variant="link"
          class="text-blue"
          @click="showCreateGroupInput = true"
        />
        <GekkoButton
          v-else
          :icon="'fa-solid fa-arrow-left'"
          :text="$gettext('Add existing')"
          variant="link"
          class="text-blue"
          @click="showCreateGroupInput = false"
        />
      </div>
    </b-form-group>
  </b-form>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import ProductGroupService from '@/store/services/productgroup.service.js'
import ProductService from '@/store/services/product.service.js'

import VatInput from '@/components/VatInput.vue'
import GekkoButton from '@/components/GekkoButton'

export default {
  name: 'AddEditProduct',
  components: {
    VatInput,
    GekkoButton,
  },
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    initialGroupId: {
      type: Number,
      default: null,
    },
    allowGroupCreation: {
      type: Boolean,
      default: false,
    },
    productName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      item: {},
      groupOptions: [],
      newGroup: {},
      showCreateGroupInput: false,
      priceOptions: [
        { value: false, text: this.$gettext('Price (ex. VAT)') },
        { value: true, text: this.$gettext('Price (incl. VAT)') },
      ],
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: 'main/getCurrentCompany',
      isWaitingForResponse: 'main/isWaitingForResponse',
    }),
    isEdit() {
      return this.product?.id
    },
  },
  created() {
    this.setupData()
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE',
    }),
    setupData() {
      if (this.initialGroupId) {
        this.group = this.initialGroupId
      }
      this.getProductGroups()
      if (this.product && this.product.id) {
        this.item = Object.assign({}, this.product, {})
        if (this.item.amount_is_gross) {
          this.item.amount = this.item.total_inc_vat
        }
      } else {
        this.item = {
          amount: null,
          description: this.productName ? this.productName : null,
          amount_is_gross: false,
          archived: false,
          company_profile: this.currentCompany.id,
          total_inc_vat: false,
          vat_exempt: false,
          group: null,
        }
        if (this.initialGroupId) {
          this.item.group = this.initialGroupId
        }
        if (!this.currentCompany.default_tax_rate_exempt) {
          this.item.tax_rate = this.currentCompany.default_tax_rate
        } else {
          this.item.tax_rate = 21
          this.item.vat_exempt = true
        }
        if (this.allowGroupCreation) {
          this.newGroup.name = null
        }
      }
    },
    getProductGroups() {
      return ProductGroupService.list({ company_profile: this.currentCompany.id }).then((response) => {
        this.groupOptions = response.data
        this.groupOptions.unshift({ id: null, name: '---' })
      })
    },
    save() {
      if (!this.currentCompany.is_premium) {
        this.$store.commit('togglePremiumModal', 'web_product_add')
        return
      }

      if (this.isEdit) {
        this.saveEdit()
      } else {
        this.saveCreate()
      }
    },
    saveCreate() {
      if (this.allowGroupCreation && this.showCreateGroupInput) {
        this.setWaitingForResponse(true)
        ProductGroupService.create({ company_profile: this.currentCompany.id, ...this.newGroup })
          .then((response) => {
            this.item.group = response.data.id
            this.createProduct(true)
          })
          .catch((response) => {
            if (response.data['non_field_errors']) {
              this.showAlert('group')
            }
            this.setWaitingForResponse(false)
          })
      } else {
        this.setWaitingForResponse(true)
        if (!this.item.group) {
          delete this.item.group
        }
        this.createProduct(false)
      }
    },
    saveEdit() {
      this.setWaitingForResponse(true)
      ProductService.update(this.product.id, this.item)
        .then(() => {
          this.$emit('product-updated', this.product.id)
        })
        .catch((response) => {
          if (response.data['non_field_errors']) {
            this.showAlert()
          }
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
    createProduct(withGroup = false) {
      ProductService.create(this.item)
        .then((response) => {
          this.$emit('product-created', response.data)
          if (withGroup) {
            this.$emit('group-created')
          }
        })
        .catch((response) => {
          if (withGroup) {
            this.getProductGroups().then(() => {
              this.showCreateGroupInput = false
            })
          }

          if (response.data['non_field_errors']) {
            this.showAlert()
          } else if (response.data['amount']) {
            this.showAlert('amount')
          } else {
            this.showAlert('general')
          }
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
    showAlert(type = 'product') {
      let msg = this.$gettext('A product with this name already exists.')
      if (type === 'group') {
        msg = this.$gettext('A group with this name already exists.')
      } else if (type === 'amount') {
        msg = this.$gettext('Please enter a valid amount.')
      } else if (type === 'general') {
        msg = this.$gettext('An error occurred while saving your product. Please check the details and try again.')
      }
      this.$store.commit('setAlert', {
        title: this.$gettext('Could not save'),
        text: msg,
        variant: 'warning',
        duration: 2,
      })
    },
  },
}
</script>
